/// <amd-module name="Test/Analytics/Widgets/DetailsWidget/DetailsWidgetSource" />
import { ODataQueryOptions } from "Analytics/Common/ODataQueryOptions";
import { IODataQueryResponse } from "Analytics/Shared/Common/Types";
import { DetailsWidgetColumnNames, DetailsWidgetOdataPropertyNames } from "Test/Analytics/Widgets/DetailsWidget/Constants";
import { TestResourceEntityNames } from "Test/ManualTestReports/TestReportsCommon/Constants";
import { IFilterData } from "Test/ManualTestReports/TestReportsCommon/Contracts";
import { FilterHelper } from "Test/ManualTestReports/TestReportsCommon/FilterHelper";
import * as CommonResources from "Test/ManualTestReports/TestReportsCommon/Resources";
import { TestProgressReportSourceBase } from "Test/ManualTestReports/TestReportsCommon/TestProgressReportSourceBase";
import { format } from "VSS/Core/Util/String";
import { SortOrder } from "VSSUI/Table";
import { IDetailsWidgetItem, ITestPlanSummaryOdataResponse } from "./Contracts";

var propNames = DetailsWidgetOdataPropertyNames;

export class DetailsWidgetSource extends TestProgressReportSourceBase {
    public async getTestPlanSummaryDetails(filterData?: IFilterData, sortOptions?: { columnName: string; sortOrder: SortOrder }, lastSelectedPlanId?: number): Promise<IDetailsWidgetItem[]> {
        const queryOptions = {
            entityType: TestResourceEntityNames.Points,
            project: this._getProjectId()
        } as ODataQueryOptions;

        var groupByStr = this._getGroupByStrForRootLevelItems();
        const testProgressFilterState = !!filterData ? await this._getCurrentFilterState(filterData.filterState) : await this._getCurrentFilterState(undefined, lastSelectedPlanId);
        const filterStr = FilterHelper.getFilterString(testProgressFilterState);
        if (filterStr) {
            queryOptions.$apply = `${filterStr}/${groupByStr}`;
        }
        else {
            queryOptions.$apply = `${groupByStr}`;
        }
        queryOptions.$orderby = this._getOrderByClause(0, sortOptions);

        return this.queryOData(queryOptions).then((oDataQueryResponse: IODataQueryResponse) => {
            return this._createTestContainerSummarizationList(oDataQueryResponse, 0);
        });
    }

    public async getTestSuiteChildren(
        suiteId: number,
        suiteDepth: number,
        filterData?: IFilterData,
        sortOptions?: { columnName: string; sortOrder: SortOrder },
        lastSelectedPlanId?: number
    ): Promise<IDetailsWidgetItem[]> {
        const queryOptions = {
            entityType: TestResourceEntityNames.Points,
            project: this._getProjectId()
        } as ODataQueryOptions;

        var groupByStr = this._getGroupByStrForChildItems(suiteDepth);
        const testProgressFilterState = !!filterData ? await this._getCurrentFilterState(filterData.filterState) : await this._getCurrentFilterState(undefined, lastSelectedPlanId);
        const filterStr = FilterHelper.getFilterString(testProgressFilterState, this._getFilterSubTextForSuiteDepth(suiteId, suiteDepth));
        if (filterStr) {
            queryOptions.$apply = `${filterStr}/${groupByStr}`;
        }

        queryOptions.$apply = `${filterStr}/${groupByStr}`;
        queryOptions.$orderby = this._getOrderByClause(suiteDepth + 1, sortOptions);

        return this.queryOData(queryOptions).then((oDataQueryResponse: IODataQueryResponse) => {
            return this._createTestContainerSummarizationList(oDataQueryResponse, suiteDepth);
        });
    }

    private _createTestContainerSummarizationList(oDataQueryResponse: IODataQueryResponse, rowDepth: number): IDetailsWidgetItem[] {
        var response: IDetailsWidgetItem[] = [];

        if (!oDataQueryResponse || !oDataQueryResponse.value) {
            throw new Error(CommonResources.OdataResponseEmpty);
        }

        oDataQueryResponse.value.forEach((row: ITestPlanSummaryOdataResponse) => {
            response.push({
                id: row.TestSuite[`IdLevel${rowDepth + 1}`],
                depth: rowDepth + 1,
                passedPercentage: row.PassedPercentage,
                failedPercentage: row.FailedPercentage,
                runPercentage: row.RunPercentage,
                notRunCount: row.NotRunCount,
                pointCount: row.TotalCount,
                containerName: row.TestSuite.TestPlanTitle ? row.TestSuite.TestPlanTitle : row.TestSuite[`TitleLevel${rowDepth + 1}`],
                planId: row.TestSuite.TestPlanId
            });
        });

        return response;
    }

    private _getGroupByStrForRootLevelItems(): string {
        return `groupby((${propNames.testPlanId},${propNames.testPlanTitle}, ${propNames.testSuiteId}${1}),aggregate($count as ${propNames.totalCount},
            cast(LastResultOutcome eq 'Passed', Edm.Int32) with sum as ${propNames.passedCount},         
            cast(LastResultOutcome eq 'Failed', Edm.Int32) with sum as ${propNames.failedCount},
            cast(LastResultOutcome eq 'None', Edm.Int32) with sum as ${propNames.notRunCount},
            cast(LastResultOutcome ne 'None', Edm.Int32) with sum as ${propNames.runCount}))/
            compute(${propNames.runCount} mul 100 div ${propNames.totalCount} as ${propNames.runPercentage}, 
                ${propNames.notRunCount} mul 100 div ${propNames.totalCount} as ${propNames.notRunPercentage},
                iif(${propNames.totalCount} gt ${propNames.notRunCount}, ${propNames.passedCount} mul 100 div ${propNames.runCount},0) as  ${propNames.passedPercentage},
                iif(${propNames.totalCount} gt ${propNames.notRunCount}, ${propNames.failedCount} mul 100 div ${propNames.runCount},0) as  ${propNames.failedPercentage})`;
    }

    private _getGroupByStrForChildItems(suiteDepth: number): string {
        return `groupby((${propNames.testPlanId},${propNames.testSuiteTitle}${suiteDepth + 1},${propNames.testSuiteId}${suiteDepth + 1}),aggregate($count as ${propNames.totalCount}, 
            cast(LastResultOutcome eq 'Passed', Edm.Int32) with sum as ${propNames.passedCount},         
            cast(LastResultOutcome eq 'Failed', Edm.Int32) with sum as ${propNames.failedCount},
            cast(LastResultOutcome eq 'None', Edm.Int32) with sum as  ${propNames.notRunCount},
            cast(LastResultOutcome ne 'None', Edm.Int32) with sum as ${propNames.runCount}))/
            compute(${propNames.runCount} mul 100 div ${propNames.totalCount} as ${propNames.runPercentage}, 
                ${propNames.notRunCount} mul 100 div ${propNames.totalCount} as ${propNames.notRunPercentage},
                iif(${propNames.totalCount} gt ${propNames.notRunCount}, ${propNames.passedCount} mul 100 div ${propNames.runCount},0) as ${propNames.passedPercentage},
                iif(${propNames.totalCount} gt ${propNames.notRunCount}, ${propNames.failedCount} mul 100 div ${propNames.runCount},0) as  ${propNames.failedPercentage})`;
    }

    private _getFilterSubTextForSuiteDepth(suiteId: number, suiteDepth: number) {
        let filterStr = `(TestSuite/IdLevel${suiteDepth} eq ${suiteId} and TestSuite/IdLevel${suiteDepth + 1} ne null)`;
        return filterStr;
    }

    private _getOrderByClause(depth: number, sortOptions: { columnName: string; sortOrder: SortOrder } | undefined): string {
        if (!sortOptions) {
            return `${propNames.runPercentage} desc`;
        }
        let columnName = "";
        switch (sortOptions.columnName) {
            case DetailsWidgetColumnNames.runPercentage:
                columnName = DetailsWidgetOdataPropertyNames.runPercentage;
                break;
            case DetailsWidgetColumnNames.passedPercentage:
                columnName = DetailsWidgetOdataPropertyNames.passedPercentage;
                break;
            case DetailsWidgetColumnNames.notRunCount:
                columnName = DetailsWidgetOdataPropertyNames.notRunCount;
                break;
            case DetailsWidgetColumnNames.failedPercentage:
                columnName = DetailsWidgetOdataPropertyNames.failedPercentage;
                break;
            case DetailsWidgetColumnNames.pointCount:
                columnName = DetailsWidgetOdataPropertyNames.totalCount;
                break;
            case DetailsWidgetColumnNames.itemName:
            default:
                if (depth === 0) {
                    columnName = DetailsWidgetOdataPropertyNames.testPlanTitle;
                } else {
                    columnName = `${DetailsWidgetOdataPropertyNames.testSuiteTitle}${depth}`;
                }
        }
        switch (sortOptions.sortOrder) {
            case SortOrder.ascending:
                return format("{0} asc", columnName);
            case SortOrder.descending:
                return format("{0} desc", columnName);
        }
    }
}
